import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import PaymentModal from '@/components/PaymentModal';
import ServiceMeta from '@/components/ServiceMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Music, Upload, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const DistributionServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    artist_name: '',
    title: '',
    genre: ''
  });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const genres = [
    'Afrobeat', 'Hip-Hop', 'RnB', 'Pop', 'Gospel', 
    'Rumba', 'Soukous', 'Ndombolo', 'Reggae', 'Autre'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!audioFile || !coverFile) {
      toast.error('Veuillez uploader l\'audio et la cover');
      return;
    }
    
    // Ouvrir le modal de paiement au lieu de soumettre directement
    setShowPaymentModal(true);
  };

  const handlePaymentInitiate = async (paymentData) => {
    const formDataToSend = new FormData();
    formDataToSend.append('artist_name', formData.artist_name);
    formDataToSend.append('title', formData.title);
    formDataToSend.append('genre', formData.genre);
    formDataToSend.append('audio', audioFile);
    formDataToSend.append('cover', coverFile);
    formDataToSend.append('operator', paymentData.operator);
    formDataToSend.append('phone_number', paymentData.phone_number);
    
    const response = await api.post('/service/payment/distribution', formDataToSend, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data;
  };

  const handlePaymentSuccess = () => {
    setSubmitted(true);
    toast.success('✅ Paiement réussi ! Votre distribution sera traitée sous 48h.');
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <ServiceMeta 
          title="Distribution Musicale - Spotify, Apple Music, Deezer"
          description="Distribuez votre musique sur toutes les plateformes de streaming (Spotify, Apple Music, Deezer, YouTube Music) pour seulement 15$ via AGRP BOX."
          url="/distribuer-musique"
        />
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Paiement Réussi ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Votre musique sera distribuée sur toutes les plateformes (Spotify, Apple Music, Deezer, etc.) sous 48-72h.
              </p>
              <div className="bg-zinc-900 rounded-lg p-4 mb-6">
                <p className="text-sm text-zinc-400">
                  <strong className="text-white">Prochaines étapes :</strong>
                </p>
                <ul className="text-left text-sm text-zinc-400 mt-2 space-y-1">
                  <li>✓ Paiement confirmé (15$)</li>
                  <li>✓ En attente de traitement par l'équipe AGRP BOX</li>
                  <li>⏳ Distribution sous 48-72h</li>
                  <li>📧 Vous recevrez un email de confirmation</li>
                </ul>
              </div>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#E50914]">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
        
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={15}
          serviceName="Distribution Musicale"
          onPaymentInitiate={handlePaymentInitiate}
          onPaymentSuccess={handlePaymentSuccess}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <ServiceMeta 
        title="Distribution Musicale - Spotify, Apple Music, Deezer"
        description="Distribuez votre musique sur toutes les plateformes de streaming (Spotify, Apple Music, Deezer, YouTube Music) pour seulement 15$ via DYVY'S Afrique."
        url="/distribuer-musique"
      />
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Music className="text-purple-500" size={32} />
              Mettre Sa Musique Sur Les Plateformes - 15$
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Paiement direct. Dès que vous déposez la chanson et payez, c'est confirmé automatiquement.
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Nom de l'Artiste *</Label>
                <Input
                  value={formData.artist_name}
                  onChange={(e) => setFormData({...formData, artist_name: e.target.value})}
                  required
                  placeholder="Ex: Raoul MLELO"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Titre de la Chanson *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder="Ex: Mon Hit"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Genre Musical *</Label>
                <Select value={formData.genre} onValueChange={(value) => setFormData({...formData, genre: value})}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectValue placeholder="Choisir un genre" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {genres.map(genre => (
                      <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Fichier Audio (MP3, WAV) *</Label>
                <div className="mt-2">
                  <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-purple-500 transition-colors bg-zinc-900">
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 text-zinc-500" size={32} />
                      <p className="text-sm text-zinc-400">
                        {audioFile ? audioFile.name : 'Cliquez pour uploader votre musique'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setAudioFile(e.target.files[0])}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              </div>

              <div>
                <Label>Cover / Pochette (JPG, PNG) *</Label>
                <div className="mt-2">
                  <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-purple-500 transition-colors bg-zinc-900">
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 text-zinc-500" size={32} />
                      <p className="text-sm text-zinc-400">
                        {coverFile ? coverFile.name : 'Cliquez pour uploader la cover'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCoverFile(e.target.files[0])}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              </div>

              <div className="bg-purple-900/20 border border-purple-800 rounded-lg p-4 text-sm text-purple-200">
                <strong>📋 Comment ça marche :</strong>
                <ol className="mt-2 space-y-1 ml-4 list-decimal">
                  <li>Déposez votre musique + cover</li>
                  <li>Payez 15$ directement</li>
                  <li>Confirmation automatique immédiate</li>
                  <li>Distribution sur Spotify, Apple Music, Deezer, etc.</li>
                </ol>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:opacity-90 py-6 text-lg font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'PAYER ET SOUMETTRE (15$)'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
      
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        amount={15}
        serviceName="Distribution Musicale"
        onPaymentInitiate={handlePaymentInitiate}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default DistributionServicePage;
